import { useState } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { CustomNumberFlow } from '@/shared/components/CustomNumberFlow';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { Divider } from '@/shared/components/Divider';
import { PositionsValueChart } from '@/shared/components/lending/PositionsValueChart';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChain } from '@/shared/components/logo/TokenWithChain';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { TimeframeTabSelect } from '@/shared/components/TimeframeTabSelect';
import { FIRST_SUPPLY_DATE } from '@/shared/constants';
import { getLpTotalSupplyPnl } from '@/shared/graphql/lp/lending';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useInterval from '@/shared/hooks/useInterval';
import { useLpLendingPositions } from '@/shared/hooks/useLpLendingPositions';
import {
  type PositionEarnedFees,
  useLpPositionEarnedFees,
} from '@/shared/hooks/useLpPositionEarnedFees';
import useTimeframeSelection from '@/shared/hooks/useTimeframeSelection';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/large/MinusCircle';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import { apyToText } from '@/shared/utils/boost';
import { chainLabel } from '@/shared/utils/chainflip';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import { EarnedSupplyFeesChart } from './EarnedFeesChart';
import { SkeletonLine } from '../atoms/LoadingSkeleton';
import { TabSelect } from '../TabSelect';

const EarnedFeesCellContent = ({
  positionEarnedFees,
  asset,
  isLoading,
}: {
  positionEarnedFees: PositionEarnedFees | undefined;
  asset: ChainflipAsset;
  isLoading: boolean;
}) => {
  const feesUsd = positionEarnedFees?.earnedUsd ?? 0;
  const feesAmount = positionEarnedFees?.earnedAmount ?? TokenAmount.ZERO;
  return (
    <div className="flex flex-col text-14">
      {isLoading ? (
        <SkeletonLine width={90} height={14} />
      ) : (
        <div className={feesUsd >= 0 ? 'text-cf-green-3' : 'text-cf-red-1'}>
          {feesAmount.toCapped()} {chainflipAssetMap[asset].symbol}
        </div>
      )}
      {isLoading ? (
        <SkeletonLine width={50} height={14} />
      ) : (
        <div className="text-cf-light-2">{formatUsdValue(feesUsd, undefined, true)}</div>
      )}
    </div>
  );
};
export const SupplyPosition = ({
  lpIdSs58,
  openSupplyModal,
  openRemoveSupplyModal,
}: {
  lpIdSs58: string;
  openSupplyModal?: (token: ChainflipToken) => void;
  openRemoveSupplyModal?: (token: ChainflipToken) => void;
}) => {
  const [endDate, setEndDate] = useState(new Date());
  useInterval(() => setEndDate(new Date()), 30_000);

  const { data: positions, weightedAverageApy } = useLpLendingPositions({
    idSs58: lpIdSs58,
  });

  const { earnedFeesByAsset, isLoading: earnedFeesIsLoading } = useLpPositionEarnedFees(
    lpIdSs58,
    endDate,
  );

  const { selectedTimeframe, setSelectedTimeframe, timeframeTabOptions } = useTimeframeSelection({
    initialTimeframe: '1M',
    allTimeStartDate: 'firstSupply',
  });

  const { data: totalPnlData, isLoading: totalPnlDataIsLoading } = useGqlQuery(
    getLpTotalSupplyPnl,
    {
      variables: {
        minTimestamp: new Date(FIRST_SUPPLY_DATE).toISOString(),
        maxTimestamp: endDate.toISOString(),
        idSs58: lpIdSs58,
      },
      refetchInterval: 30_000,
      context: { clientName: 'lpProcessor' },
    },
  );

  const totalPnlUsd = totalPnlData?.account?.snapshots.agg?.sum?.supplyPnl1HUsd ?? 0;

  const totalSupplied = positions.reduce(
    (acc, position) => acc + parseFloat(position.totalAmountUsd),
    0,
  );

  const chartTabSelectOptions = [
    {
      label: 'Position Value',
    },
    {
      label: 'Earnings',
    },
  ];
  const [selectedChartTab, setSelectedChartTab] = useState(chartTabSelectOptions[0]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="flex flex-col rounded-md border border-cf-gray-3-5 bg-cf-gray-3 shadow-grayPop3">
        <div className="flex flex-col gap-y-6 p-6">
          <div className="flex items-center gap-x-10">
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Total Supplied{' '}
                <QuestionMarkTooltip content="The total amount of liquidity you’ve supplied across all assets." />
              </div>
              <div className="font-aeonikMedium text-20 text-cf-white">
                <CustomNumberFlow value={formatUsdValue(totalSupplied)} />
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Supply APY{' '}
                <QuestionMarkTooltip content="Your combined APY across all supplied assets. We take hourly snapshots of fees earned in the pool, average them over 7 days, and project the result over a year." />
              </div>
              <div className="font-aeonikMedium text-20 text-cf-white">
                <CustomNumberFlow value={apyToText(weightedAverageApy)} />
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Total Earned{' '}
                <QuestionMarkTooltip content="All fees earned across all your supply positions over time. Includes earnings from past and current positions." />
              </div>
              {totalPnlDataIsLoading ? (
                <SkeletonLine width={80} height={26} />
              ) : (
                <div className="font-aeonikMedium text-20 text-cf-white">
                  {Number(totalPnlUsd) > 0 && '+'}
                  <CustomNumberFlow value={formatUsdValue(totalPnlUsd, undefined, true)} />
                </div>
              )}
            </div>
          </div>
          <Divider className="border-cf-gray-3-5" />
        </div>
        <TableV2
          className="border-opacity-0"
          thClassName="font-aeonikMedium text-cf-light-2"
          columns={[
            'Asset',
            'Supplied Amount',
            'APY',
            {
              name: 'Earned',
              tooltip:
                'Fees earned by this asset in your current position only. Does not include earnings from previous or closed positions.',
            },
            ...(openRemoveSupplyModal && openSupplyModal
              ? [{ name: '', alignment: 'right' } as const]
              : []),
          ]}
          rows={positions.map((position) => (
            <tr key={position.asset}>
              <td className="text-cf-white">
                <div className="flex flex-col gap-y-1">
                  <div className="flex items-center gap-x-2 font-aeonikMedium text-cf-white">
                    <div className="relative h-[37px] w-[35px]">
                      <div className="absolute bottom-px">
                        <TokenWithChain
                          chainLogo={<ChainLogo chain={chainflipAssetMap[position.asset].chain} />}
                          tokenLogo={<TokenLogo size="large" token={position.asset} />}
                          size="large"
                        />
                      </div>
                    </div>
                    <div className="font-aeonikMedium">
                      <span className="text-white">{chainflipAssetMap[position.asset].symbol}</span>
                      <div className="text-cf-light-2">
                        on {chainLabel(chainflipAssetMap[position.asset].chain.chainflipId)}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td className="font-aeonikMedium text-cf-white">
                {TokenAmount.fromAsset(position.totalAmount, position.asset).toFormatted()}{' '}
                {chainflipAssetMap[position.asset].symbol}
                <div className="text-cf-light-2">{formatUsdValue(position.totalAmountUsd)}</div>
              </td>
              <td className="font-aeonikMedium text-cf-white">
                {apyToText(position.projectedApy)}
              </td>
              <td className="font-aeonikMedium">
                <EarnedFeesCellContent
                  positionEarnedFees={earnedFeesByAsset.get(position.asset)}
                  asset={position.asset}
                  isLoading={earnedFeesIsLoading}
                />
              </td>
              {openRemoveSupplyModal && openSupplyModal && (
                <td className="text-right">
                  <div className="flex justify-end gap-x-2">
                    <ReadonlyButton
                      size="small"
                      type="secondary-standard"
                      className="!min-w-[30px]"
                      aria-label="remove-supply"
                      onClick={() => {
                        openRemoveSupplyModal(chainflipAssetMap[position.asset]);
                      }}
                    >
                      <MinusCircleIcon className="text-cf-red-2" />
                    </ReadonlyButton>
                    <ReadonlyButton
                      size="small"
                      type="secondary-standard"
                      className="!min-w-[30px]"
                      aria-label="add-supply"
                      onClick={() => {
                        openSupplyModal(chainflipAssetMap[position.asset]);
                      }}
                    >
                      <PlusCircleIcon className="text-cf-green-3" />
                    </ReadonlyButton>
                  </div>
                </td>
              )}
            </tr>
          ))}
          emptyRowsContent="No lending positions"
        />
      </div>
      <div className="flex flex-col gap-y-2 rounded-md border border-cf-gray-3-5 bg-cf-gray-3 p-6 shadow-grayPop3">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-y-4">
            <TabSelect
              selectedTab={selectedChartTab}
              onSelectionChange={setSelectedChartTab}
              tabs={chartTabSelectOptions}
              pillHeight={26}
              containerMargin={2}
              pillWidth={121}
              btnClasses="text-12"
            />
          </div>
          <div>
            <TimeframeTabSelect
              selected={selectedTimeframe}
              onChange={setSelectedTimeframe}
              timeframeTabs={timeframeTabOptions}
            />
          </div>
        </div>
        {selectedChartTab.label === 'Position Value' ? (
          <PositionsValueChart
            days={selectedTimeframe.value}
            idSs58={lpIdSs58}
            currentSupplied={totalSupplied}
          />
        ) : (
          <EarnedSupplyFeesChart days={selectedTimeframe.value} idSs58={lpIdSs58} />
        )}
      </div>
    </div>
  );
};
